import React, { useCallback, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@/hooks/use.redux';
import { FancyAlert } from 'react-native-expo-fancy-alerts';
import { Box } from '@/components/base/box';
import { AppTextView } from '@/components/base/app.text.view';
import { AppIconButton } from '@/components/base/app.icon.button';
import { AppButton } from '@/components/base/app.button';
import styled, { useTheme } from 'styled-components/native';
import { actionRemoveAlert } from '@/redux';
import { GlobalAlert } from '@/components/compose/global.alert';

function BaseAlertProvider({ children }) {
  const dispatch = useAppDispatch();
  const fancyAlerts = useAppSelector((state) => state.generalReducer.alerts).filter((alert) => alert.type === 'fancy');

  const theme = useTheme();

  const alert = fancyAlerts[0];

  const mode = alert?.mode ?? 'warn';
  const modeColors = {
    error: theme.colors.modal.error,
    warn: theme.colors.modal.warn,
    info: theme.colors.modal.info,
  };
  const modeVariants = { error: 'red', warn: 'orange', info: 'green' } as const;

  useEffect(() => {
    if (!alert?.duration) return;
    const id = setTimeout(() => {
      alert.onClose?.();
      dispatch(actionRemoveAlert(alert.id));
    }, alert.duration);
    return () => clearTimeout(id);
  }, [alert, dispatch]);

  const onClose = useCallback(() => {
    alert.onClose?.();
    dispatch(actionRemoveAlert(fancyAlerts[0].id));
  }, [alert, dispatch, fancyAlerts]);

  const onYesPress = useCallback(() => {
    alert?.onYes?.();
    onClose();
  }, [alert, onClose]);

  const onNoPress = useCallback(() => {
    alert?.onNo?.();
    onClose();
  }, [alert, onClose]);

  return (
    <>
      {children}
      {alert && (
        <AlertFancyAlert
          visible={!!alert}
          onRequestClose={onClose}
          icon={
            <IconBox $bgColor={modeColors[mode]}>
              <AppIconButton icon={alert.mode === 'info' ? 'info' : 'warning'} color={'white'} />
            </IconBox>
          }
        >
          <AlertMessageBox fullWidth alignItems={'center'}>
            <AlertMessageText lineBreakMode={'tail'} text={alert.message} />
            {alert.onYes ? (
              <AlertButtonRow>
                <AlertButton rounded variant={modeVariants[mode]} title={'باشه'} onPress={onYesPress} />
                <AlertButton rounded variant={'grey'} title={'خیر'} onPress={onNoPress} />
              </AlertButtonRow>
            ) : (
              <AlertSingleButton rounded variant={modeVariants[mode]} title={'باشه'} onPress={onClose} />
            )}
          </AlertMessageBox>
        </AlertFancyAlert>
      )}
      <GlobalAlert />
    </>
  );
}

const AlertMessageBox = styled(Box)`
  margin-top: -16px;
`;

const AlertMessageText = styled(AppTextView)`
  margin-bottom: 32px;
  text-align: center;
  line-height: 25px;
`;

const AlertButtonRow = styled(Box)`
  flex-direction: row;
  gap: 9px;
  width: 100%;
`;

const AlertButton = styled(AppButton)`
  flex: 1;
  margin-bottom: 9px;
`;

const AlertSingleButton = styled(AppButton)`
  align-self: stretch;
  width: 100%;
  margin-bottom: 9px;
`;

const AlertFancyAlert = styled(FancyAlert)`
  background-color: white;
  max-width: 100px;
`;

const IconBox = styled(Box)<{ $bgColor: string }>`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${({ $bgColor }) => $bgColor};
  border-radius: 50px;
  width: 100%;
`;

export const AlertProvider = React.memo(BaseAlertProvider);
